import { ProductType } from '@tink/core/src/types';
import { useMachine } from '@xstate/react';
import { useEffect, useMemo } from 'react';
import {
  LoadingOverlay as LoadingOverlayImport,
  LoadingOverlayProps,
} from '../../../core/screens/LoadingOverlay/LoadingOverlay';
import { HandoffCredentialsAction } from '../../../models/handoffSession';
import { ProductDimension } from '../../../models/product';
import { ProviderEntity } from '../../../models/provider';
import { routeBuilder } from '../../../routes/routeBuilder';
import {
  createHandoffCreatorMachine,
  HandoffCreatorMachineContext,
} from '../../machines/handoffCreatorMachine';
import { CredentialsEntity } from '../../../models/credentials';
import { ApplicationErrorPayload } from '../../../stores/app-state-store';
import { BackButton } from '../../../components/organisms/Header/HeaderMolecule';
import {
  HandoffCodeScanned as HandoffCodeScannedImport,
  HandoffCodeScannedOwnProps,
} from './HandoffCodeScanned/HandoffCodeScanned';
import { QrHandoff as QrHandoffImport, QrHandoffProps } from './QrHandoff/QrHandoff';

export type HandoffCreatorMachineViewProps = {
  provider: ProviderEntity;
  version: string;
  productType: ProductType;
  productDimension: ProductDimension;
  trackingId: string;
  userAccessToken?: string;
  locationOrigin: string;
  userHintId?: string;
  handoffCredentialsAction: HandoffCredentialsAction;
  forceWebFlow: () => void;
  onDone: (credentials: CredentialsEntity) => void;
  onError: (error: ApplicationErrorPayload, credentials?: CredentialsEntity) => void;
  initialState?: string;
  initialContext?: HandoffCreatorMachineContext;
  LoadingOverlay?: React.ComponentType<LoadingOverlayProps>;
  QrHandoff?: React.ComponentType<QrHandoffProps>;
  HandoffCodeScanned?: React.ComponentType<HandoffCodeScannedOwnProps>;
} & BackButton;

export const HandoffCreatorMachineView = ({
  userAccessToken,
  provider,
  version,
  productType,
  productDimension,
  trackingId,
  locationOrigin,
  userHintId,
  handoffCredentialsAction,
  forceWebFlow,
  onBack,
  onDone,
  onError,
  LoadingOverlay = LoadingOverlayImport,
  QrHandoff = QrHandoffImport,
  HandoffCodeScanned = HandoffCodeScannedImport,
  initialState,
  initialContext,
}: HandoffCreatorMachineViewProps) => {
  const machine = useMemo(
    () =>
      createHandoffCreatorMachine({
        userAccessToken,
        provider,
        productType,
        productDimension,
        version,
        trackingId,
        userHintId,
        handoffCredentialsAction,
        initialState,
        initialContext,
      }),
    [
      userAccessToken,
      provider,
      version,
      productType,
      productDimension,
      trackingId,
      userHintId,
      handoffCredentialsAction,
      initialState,
      initialContext,
    ]
  );

  const [current, send] = useMachine(machine);

  const cancelConsumedHandoff = () => {
    send('EVENT_CANCEL_CONSUMED_HANDOFF_SESSION');
  };

  useEffect(() => {
    if (current.matches('processCredentialsUpdated') && current.context.credentials) {
      onDone(current.context.credentials);
    }
  }, [current, onDone]);

  useEffect(() => {
    if (current.matches('processError') && current.context.error) {
      onError(current.context.error, current.context.credentials);
    }
  }, [current, onError]);

  useEffect(() => {
    if (current.matches('processCredentialsError') && current.context.credentials) {
      onDone(current.context.credentials);
    }
  }, [current, onDone]);

  if (
    current.matches('decideIfStartHandoffSession') ||
    current.matches('processCreateUrlHandoffSession')
  ) {
    return <LoadingOverlay />;
  }

  if (
    current.matches('uiPollingHandoffSession') ||
    current.matches('decideHandoffSessionHandling')
  ) {
    return (
      <QrHandoff
        userHintId={userHintId}
        provider={provider}
        qrCodeValue={routeBuilder.buildShortHandoffUrlForMobile.build({
          origin: locationOrigin,
          version,
          handoffSessionId: current.context.handoffSession ? current.context.handoffSession.id : '',
        })}
        forceWebFlow={forceWebFlow}
        onBack={onBack}
      />
    );
  }

  if (
    current.matches('uiPollingHandoffCredentials') ||
    current.matches('decideCredentialsHandling') ||
    current.context.handoffSession?.isConsumed
  ) {
    return (
      <HandoffCodeScanned
        provider={provider}
        credentials={current.context.credentials}
        onCancel={cancelConsumedHandoff}
      />
    );
  }

  return <LoadingOverlay />;
};
